import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './CustomSelect.module.css';

export interface CustomSelectOption {
    value: string;
    label: string;
}

export interface CustomSelectProps {
    /** Unique identifier for the select */
    id?: string;
    /** Label text displayed above the select */
    label?: string;
    /** Placeholder text when no option is selected */
    placeholder?: string;
    /** Array of options to display */
    options: CustomSelectOption[];
    /** Currently selected value */
    value: string;
    /** Callback when selection changes */
    onChange: (value: string) => void;
    /** Whether the select is disabled */
    disabled?: boolean;
    /** Whether the select should take full width */
    fullWidth?: boolean;
    /** Additional class name for the container */
    className?: string;
    /** Hide the label visually (still accessible) */
    hideLabel?: boolean;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
    id,
    label,
    placeholder = 'Select',
    options,
    value,
    onChange,
    disabled = false,
    fullWidth = true,
    className = '',
    hideLabel = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdown on escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleSelect = useCallback(
        (optionValue: string) => {
            onChange(optionValue);
            setIsOpen(false);
        },
        [onChange],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent, optionValue: string) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelect(optionValue);
            }
        },
        [handleSelect],
    );

    const selectedOption = options.find((opt) => opt.value === value);
    const displayText = selectedOption?.label || '';

    return (
        <div
            className={`${styles.Container} ${fullWidth ? styles.FullWidth : ''} ${className}`}
            ref={containerRef}
        >
            {label && (
                <label
                    htmlFor={id}
                    className={`${styles.Label} ${hideLabel ? styles.LabelHidden : ''}`}
                >
                    {label}
                </label>
            )}
            <div className={styles.SelectWrapper}>
                <button
                    type="button"
                    id={id}
                    className={`${styles.Trigger} ${isOpen ? styles.TriggerOpen : ''} ${
                        disabled ? styles.TriggerDisabled : ''
                    }`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    {value ? (
                        displayText
                    ) : (
                        <span className={styles.Placeholder}>{placeholder}</span>
                    )}
                </button>
                {isOpen && (
                    <div className={styles.Dropdown} role="listbox">
                        {options.map((option) => {
                            const isSelected = value === option.value;
                            return (
                                <div
                                    key={option.value}
                                    className={`${styles.Option} ${isSelected ? styles.OptionSelected : ''}`}
                                    onClick={() => handleSelect(option.value)}
                                    onKeyDown={(e) => handleKeyDown(e, option.value)}
                                    role="option"
                                    aria-selected={isSelected}
                                    tabIndex={0}
                                >
                                    {option.label}
                                </div>
                            );
                        })}
                        {options.length === 0 && (
                            <div className={styles.NoOptions}>No options available</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomSelect;

